import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';
import Notifications from '../../models/Notifications';
import AugmentedNotifications from '../../models/@aggregates/AugmentedNotifications';
import { NotificationType } from "@leedpulse-monorepo/enums";
import { IContact } from '@leedpulse-monorepo/interfaces';

async function getAugmentedNotifications(
  notifications: Notifications,
  withSubscription = false
): Promise<AugmentedNotifications> {
  try {
    const db = firestore;
    const contactDoc = await getDoc(
      doc(db, FirestoreCollections.contacts, notifications.idContact)
    );

    if (!contactDoc.exists()) {
      throw new GetNotificationsFailure();
    }

    const contact = contactDoc.data() as IContact;

    if (!withSubscription) {
      return new AugmentedNotifications(notifications, contact);
    }

    // Get subscription if augmented flag is true
    const subscriptionsCollection = collection(
      db,
      FirestoreCollections.subscriptions
    );
    const subscriptionsQuery = query(
      subscriptionsCollection,
      where('api_key', '==', notifications.idApiKey),
      limit(1)
    );

    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

    if (subscriptionsSnapshot.empty) {
      return new AugmentedNotifications(notifications, contact);
    }

    const subscription = Subscription.fromFirestore(
      subscriptionsSnapshot.docs[0]
    );

    return new AugmentedNotifications(notifications, contact, subscription);
  } catch (e) {
    console.log('error: ', e);
    throw new GetNotificationsFailure();
  }
}

async function getAllNotifications(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedSubscriptionId?: string | null,
  pendingOnly?: boolean | false,
  notificationType?: NotificationType | null,
  augmented = false,
): Promise<{
  allNotifications: AugmentedNotifications[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
}> {
  try {
    const db = firestore;
    const allNotificationsCollection = collection(
      db,
      FirestoreCollections.notifications
    );
    if (scopedSubscriptionId) {
      // We first get the corresponding subscription
      const subscriptionDoc = await getDoc(
        doc(db, FirestoreCollections.subscriptions, scopedSubscriptionId)
      );
      if (!subscriptionDoc.exists()) {
        return {
          allNotifications: [],
          lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
          totalCollectionCount: 0,
        };
      }
      const subscription = Subscription.fromFirestore(subscriptionDoc);

      // Create query conditions based on parameters
      const queryConditions = [where('id_api_key', '==', subscription.apiKey)];

      // If notificationType is provided, add filter for that specific notification type
      if (notificationType) {
        queryConditions.push(where('type', '==', notificationType));
      } else if (pendingOnly) {
        queryConditions.push(where('type', '==', NotificationType.TODO));
      } else {
        queryConditions.push(where('type', '!=', 'undefined'));
      }

      // Then we get the notifications
      const results = lastVisible
        ? await getDocs(
            query(
              allNotificationsCollection,
              ...queryConditions,
              orderBy('created', 'desc'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(
              allNotificationsCollection,
              ...queryConditions,
              orderBy('created', 'desc'),
              limit(offset)
            )
          );

      const allNotifications: AugmentedNotifications[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getAugmentedNotifications(
            Notifications.fromFirestore(doc),
            augmented
          );
        })
      );

      // Create count query with the same API key filter
      const countQueryConditions = [
        where('id_api_key', '==', subscription.apiKey),
      ];

      // Add type filter to count query if notificationType is provided
      if (notificationType) {
        countQueryConditions.push(where('type', '==', notificationType));
      } else if (pendingOnly) {
        countQueryConditions.push(where('type', '==', NotificationType.TODO));
      }

      const allSMSSnapshot = await getCountFromServer(
        query(allNotificationsCollection, ...countQueryConditions)
      );

      return {
        allNotifications,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: allSMSSnapshot.data().count ?? 0,
      };
    } else {
      // Create query conditions
      const queryConditions = [];

      // If notificationType is provided, add filter for that specific job type
      if (notificationType) {
        queryConditions.push(where('type', '==', notificationType));
      } else if (pendingOnly) {
        queryConditions.push(where('type', '==', NotificationType.TODO));
      } else {
        queryConditions.push(where('type', '!=', 'undefined'));
      }

      const results = lastVisible
        ? await getDocs(
            query(
              allNotificationsCollection,
              ...queryConditions,
              orderBy('created', 'desc'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(
              allNotificationsCollection,
              ...queryConditions,
              orderBy('created', 'desc'),
              limit(offset)
            )
          );

      const allNotifications: AugmentedNotifications[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getAugmentedNotifications(
            Notifications.fromFirestore(doc),
            augmented
          );
        })
      );

      // Create count query with job type filter if provided
      const countQueryConditions = [];
      if (notificationType) {
        countQueryConditions.push(where('type', '==', notificationType));
      } else if (pendingOnly) {
        countQueryConditions.push(where('type', '==', NotificationType.TODO));
      } 

      const allSMSSnapshot = await getCountFromServer(
        countQueryConditions.length > 0
          ? query(allNotificationsCollection, ...countQueryConditions)
          : allNotificationsCollection
      );

      return {
        allNotifications,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: allSMSSnapshot.data().count ?? 0,
      };
    }
  } catch (e) {
    console.log('error: ', e);
    throw new GetNotificationsFailure();
  }
}

export class GetNotificationsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getAllNotifications;
